/** Sempro/sidang/penelitian requests + pembimbing agreement. */

import { createDoc, updateDoc, getById } from '../service';
import { COL } from '../../collections';
import { utcNowIso } from '../../format';
import { currentPeriode } from './periode';
import { notifyRoleUser } from './notify';
import type { RequestDoc, PenelitianPlan, Role, SkripsiTeamDoc } from '../../types';

export async function registerRequest(data: {
  type: RequestDoc['type'];
  skripsiId: string;
  mahasiswaId: string;
  dosenId?: string;
  payload?: Record<string, any>;
}) {
  const p = await currentPeriode();
  return createDoc<RequestDoc>(COL.requests, {
    ...data,
    status: 'pending',
    submittedBy: data.mahasiswaId,
    tahunAjaran: p.tahunAjaran || undefined,
    semester: p.semester || undefined,
  });
}

export async function decideRequest(
  id: string,
  status: 'approved' | 'rejected' | 'revisi',
  decidedBy: string,
  note?: string,
) {
  return updateDoc<RequestDoc>(COL.requests, id, {
    status,
    decidedBy,
    decisionNote: note,
    decidedAt: utcNowIso(),
  });
}

/** Forward a request to another user (kaprodi/staf/dosen) as an action item. */
export async function forwardRequest(input: {
  requestId: string;
  requestLabel: string;
  skripsiJudul?: string;
  toUserId: string;
  /** Recipient role — required so the notification deep-link resolves. */
  toRole: Role;
  /** Role-relative destination, e.g. "verifikasi" or "permintaan". */
  link: string;
  byName: string;
  note?: string;
}) {
  return notifyRoleUser(input.toUserId, input.toRole, {
    title: `Diteruskan untuk tindak lanjut`,
    body: `${input.requestLabel}${input.skripsiJudul ? ` — ${input.skripsiJudul}` : ''} diteruskan oleh ${input.byName}${input.note ? `: ${input.note}` : ''}.`,
    type: 'warning',
    link: input.link,
  });
}

/**
 * Mahasiswa resubmits a request that staff sent back for revision. The staff
 * decision stays on record; the payload carries the resubmission stamp so the
 * queue can distinguish a fresh revision from the original attempt.
 */
export async function resubmitRequest(id: string, actorId: string) {
  const cur = await getById<RequestDoc>(COL.requests, id);
  if (!cur) throw new Error('Permintaan tidak ditemukan');
  if (cur.status !== 'revisi') throw new Error('Hanya permintaan revisi yang dapat dikirim ulang');
  if (cur.mahasiswaId !== actorId) throw new Error('Hanya pemohon yang dapat mengirim ulang');
  const now = utcNowIso();
  return updateDoc<RequestDoc>(COL.requests, id, {
    status: 'pending',
    payload: { ...(cur.payload ?? {}), resubmittedAt: now, resubmittedBy: actorId },
  });
}

/** Register the penelitian (riset lapangan) attempt with the target plan. */
export async function registerPenelitianRequest(data: {
  skripsiId: string;
  mahasiswaId: string;
  plan: PenelitianPlan;
  izinPembimbingAll: boolean;
}) {
  const p = await currentPeriode();
  return createDoc<RequestDoc>(COL.requests, {
    type: 'penelitian',
    skripsiId: data.skripsiId,
    mahasiswaId: data.mahasiswaId,
    status: 'pending',
    submittedBy: data.mahasiswaId,
    tahunAjaran: p.tahunAjaran || undefined,
    semester: p.semester || undefined,
    payload: {
      ...data.plan,
      izinPembimbingAll: data.izinPembimbingAll,
    },
  });
}

/**
 * Record a pembimbing's agreement on a sempro/sidang request. Both active
 * pembimbing must agree before staff may approve the request and schedule.
 */
export async function setPembimbingAgreement(
  id: string,
  dosenId: string,
  status: 'approved' | 'rejected',
) {
  const req = await getById<RequestDoc>(COL.requests, id);
  const payload = { ...(req?.payload ?? {}) };
  const pembimbingAgree = { ...(payload.pembimbingAgree ?? {}) };
  pembimbingAgree[dosenId] = status;
  return updateDoc<RequestDoc>(COL.requests, id, { payload: { ...payload, pembimbingAgree } });
}

export interface PembimbingAgreementInfo {
  all: boolean;
  agreed: string[];
  missing: string[];
}

/** Compute whether every active pembimbing has approved the request. */
export function pembimbingAgreementInfo(
  payload: Record<string, any> | undefined,
  team: SkripsiTeamDoc[],
): PembimbingAgreementInfo {
  const pems = team.filter(
    (t) => t.status === 'active' && (t.role === 'pembimbing-1' || t.role === 'pembimbing-2'),
  );
  const agree: Record<string, string> = payload?.pembimbingAgree ?? {};
  const agreed = pems.filter((p) => agree[p.dosenId] === 'approved').map((p) => p.dosenId);
  const missing = pems.filter((p) => agree[p.dosenId] !== 'approved').map((p) => p.dosenId);
  return { all: pems.length > 0 && missing.length === 0, agreed, missing };
}
